/**
 * Pragmatic Intelligence v2.4 — React Query Hooks
 *
 * Hooks for dual-stream analysis, Coach Lens, agent profiles,
 * signal feedback, accommodations, settings, and forecasting.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'
import type {
  DualStreamAnalysisRequest,
  DualStreamAnalysisResponse,
  CrossStreamInflectionsResponse,
  CoachRenderRequest,
  CoachRenderResponse,
  AgentProfileResponse,
  AgentConsentRequest,
  AgentConsentResponse,
  GrowthReportResponse,
  FeedbackRequest,
  FeedbackResponse,
  FeedbackHistoryResponse,
  AvailableAccommodationsResponse,
  AccommodationRequest,
  AccommodationResponse,
  TrajectoryForecastResponse,
  PragmaticUserSettings,
  PragmaticUserSettingsUpdate,
  CalibrationRequest,
  CalibrationResponse,
  SignalType,
  StreamRole,
} from '@personic/shared'

// =============================================================================
// QUERY KEYS
// =============================================================================

export const PRAGMATIC_KEYS = {
  agentProfile: (agentId: string) => ['pragmatic-agent-profile', agentId],
  agentGrowth: (agentId: string) => ['pragmatic-agent-growth', agentId],
  crossStreamInflections: (sessionId: string) => ['pragmatic-cross-stream', sessionId],
  forecast: (sessionId: string) => ['pragmatic-forecast', sessionId],
  feedbackHistory: (profileId: string, signalType?: SignalType) =>
    ['pragmatic-feedback-history', profileId, signalType],
  accommodations: () => ['pragmatic-accommodations'],
  settings: (profileId: string) => ['pragmatic-settings', profileId],
}

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Get agent pragmatic profile summary
 */
export function useAgentProfile(agentId: string) {
  return useQuery({
    queryKey: PRAGMATIC_KEYS.agentProfile(agentId),
    queryFn: async () => {
      const { data } = await api.get<AgentProfileResponse>(
        `/pragmatic/agent/${agentId}/profile`
      )
      return data
    },
    enabled: !!agentId,
  })
}

/**
 * Get agent growth tracking report
 */
export function useAgentGrowthReport(agentId: string) {
  return useQuery({
    queryKey: PRAGMATIC_KEYS.agentGrowth(agentId),
    queryFn: async () => {
      const { data } = await api.get<GrowthReportResponse>(
        `/pragmatic/agent/${agentId}/growth`
      )
      return data
    },
    enabled: !!agentId,
  })
}

/**
 * Get cross-stream inflection detections for a session
 */
export function useCrossStreamInflections(
  sessionId: string,
  affectedStream?: StreamRole
) {
  return useQuery({
    queryKey: PRAGMATIC_KEYS.crossStreamInflections(sessionId),
    queryFn: async () => {
      const params = affectedStream ? `?affected_stream=${affectedStream}` : ''
      const { data } = await api.get<CrossStreamInflectionsResponse>(
        `/pragmatic/conversations/${sessionId}/cross-stream/inflections${params}`
      )
      return data
    },
    enabled: !!sessionId,
  })
}

/**
 * Get trajectory forecast for a session
 */
export function useTrajectoryForecast(sessionId: string) {
  return useQuery({
    queryKey: PRAGMATIC_KEYS.forecast(sessionId),
    queryFn: async () => {
      const { data } = await api.get<TrajectoryForecastResponse>(
        `/pragmatic/conversations/${sessionId}/forecast`
      )
      return data
    },
    enabled: !!sessionId,
  })
}

/**
 * Get signal feedback/correction history
 */
export function useFeedbackHistory(profileId: string, signalType?: SignalType) {
  return useQuery({
    queryKey: PRAGMATIC_KEYS.feedbackHistory(profileId, signalType),
    queryFn: async () => {
      const params = signalType ? `?signal_type=${signalType}` : ''
      const { data } = await api.get<FeedbackHistoryResponse>(
        `/pragmatic/profile/${profileId}/feedback/history${params}`
      )
      return data
    },
    enabled: !!profileId,
  })
}

/**
 * Get available accommodation options (cultural + neurodivergent)
 */
export function useAvailableAccommodations() {
  return useQuery({
    queryKey: PRAGMATIC_KEYS.accommodations(),
    queryFn: async () => {
      const { data } = await api.get<AvailableAccommodationsResponse>(
        '/pragmatic/accommodations'
      )
      return data
    },
  })
}

/**
 * Get pragmatic user settings for a profile
 */
export function usePragmaticSettings(profileId: string) {
  return useQuery({
    queryKey: PRAGMATIC_KEYS.settings(profileId),
    queryFn: async () => {
      const { data } = await api.get<PragmaticUserSettings>(
        `/pragmatic/profile/${profileId}/settings`
      )
      return data
    },
    enabled: !!profileId,
  })
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Run dual-stream temporal analysis on a conversation
 */
export function useDualStreamAnalysis(sessionId: string) {
  return useMutation({
    mutationFn: async (request: DualStreamAnalysisRequest) => {
      const { data } = await api.post<DualStreamAnalysisResponse>(
        `/pragmatic/conversations/${sessionId}/analysis/dual-stream`,
        request
      )
      return data
    },
  })
}

/**
 * Render Coach Lens insights for an agent session
 */
export function useCoachLensRender(profileId: string) {
  return useMutation({
    mutationFn: async (request: CoachRenderRequest) => {
      const { data } = await api.post<CoachRenderResponse>(
        `/pragmatic/profile/${profileId}/render/coach`,
        request
      )
      return data
    },
  })
}

/**
 * Grant or revoke agent Coach consent
 */
export function useManageAgentConsent(agentId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: AgentConsentRequest) => {
      const { data } = await api.post<AgentConsentResponse>(
        `/pragmatic/agent/${agentId}/consent`,
        request
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRAGMATIC_KEYS.agentProfile(agentId) })
      queryClient.invalidateQueries({ queryKey: PRAGMATIC_KEYS.agentGrowth(agentId) })
    },
  })
}

/**
 * Submit signal feedback/correction
 */
export function useSubmitFeedback(profileId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: FeedbackRequest) => {
      const { data } = await api.post<FeedbackResponse>(
        `/pragmatic/profile/${profileId}/feedback`,
        request
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['pragmatic-feedback-history', profileId],
      })
    },
  })
}

/**
 * Set cultural and neurodivergent accommodations
 */
export function useSetAccommodations(profileId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: AccommodationRequest) => {
      const { data } = await api.post<AccommodationResponse>(
        `/pragmatic/profile/${profileId}/accommodations`,
        request
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRAGMATIC_KEYS.settings(profileId) })
    },
  })
}

/**
 * Update pragmatic user settings
 */
export function useUpdatePragmaticSettings(profileId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (update: PragmaticUserSettingsUpdate) => {
      const { data } = await api.post<PragmaticUserSettings>(
        `/pragmatic/profile/${profileId}/settings`,
        update
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRAGMATIC_KEYS.settings(profileId) })
    },
  })
}

/**
 * Update calibration weights
 */
export function useUpdateCalibration(profileId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: CalibrationRequest) => {
      const { data } = await api.post<CalibrationResponse>(
        `/pragmatic/profile/${profileId}/calibrate`,
        request
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRAGMATIC_KEYS.settings(profileId) })
    },
  })
}
